import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';
import { getDateKey } from '../../utils';
import {
  ProgramApiService,
  NextWorkout,
  ExerciseResult,
} from '../../services/api/ProgramApiService';

interface LogProgramSessionDialogProps {
  open: boolean;
  enrollmentId: number;
  nextWorkout: NextWorkout;
  onClose: () => void;
  onSessionLogged: () => void;
}

type ExerciseStatus = 'success' | 'fail';

interface ExerciseEntry {
  status: ExerciseStatus;
}

export const LogProgramSessionDialog: React.FC<LogProgramSessionDialogProps> = ({
  open,
  enrollmentId,
  nextWorkout,
  onClose,
  onSessionLogged,
}) => {
  const { t } = useLanguage();
  const [date, setDate] = useState<string>(getDateKey(new Date()));
  const [notes, setNotes] = useState('');
  const [entries, setEntries] = useState<Record<number, ExerciseEntry>>(() =>
    Object.fromEntries(
      nextWorkout.exercises.map((ex) => [ex.program_exercise_id, { status: 'success' }])
    )
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStatusChange = (exerciseId: number, status: ExerciseStatus) => {
    if (!status) return;
    setEntries((prev) => ({ ...prev, [exerciseId]: { status } }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const exercise_results: ExerciseResult[] = nextWorkout.exercises.map((ex) => {
        const entry = entries[ex.program_exercise_id];
        const success = entry.status === 'success';
        return {
          program_exercise_id: ex.program_exercise_id,
          sets_completed: ex.sets,
          reps_completed: success
            ? Array(ex.sets).fill(ex.reps)
            : Array(ex.sets).fill(ex.reps - 1),
          weight_used_kg: ex.current_weight_kg,
        };
      });

      await ProgramApiService.logSession(enrollmentId, {
        date,
        template_id: nextWorkout.template_id,
        notes: notes.trim() || undefined,
        exercise_results,
      });

      onSessionLogged();
      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to log session');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={commonDialogStyles}>
      <DialogTitle>
        {t('programs.logSession')} — {nextWorkout.template_name}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label={t('programs.date')}
          type="date"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <Divider sx={{ mb: 2 }} />

        {nextWorkout.exercises.map((ex) => (
          <Box key={ex.program_exercise_id} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body1">{ex.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ex.sets}×{ex.reps} @ {ex.current_weight_kg} kg
              </Typography>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={entries[ex.program_exercise_id]?.status ?? 'success'}
              onChange={(_, val) => handleStatusChange(ex.program_exercise_id, val)}
            >
              <ToggleButton value="success" color="success">
                <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('programs.succeeded')}
              </ToggleButton>
              <ToggleButton value="fail" color="error">
                <CloseIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('programs.failed')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ))}

        <TextField
          label={t('programs.notesOptional')}
          multiline
          rows={2}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('programs.logSession')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
